import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../../app/StoreContext';
import { useApp } from '../../app/AppContext';
import type { MenuItem } from '../../lib/types';

export function Menu() {
  const { store, addToCart } = useStore();
  const { toast } = useApp();
  const location = useLocation();
  // Categories are collapsed by default (as on the iPad); a chip, a chat
  // hand-off or a search opens them.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<{ item: MenuItem; category: string } | null>(null);
  const refs = useRef<Record<string, HTMLElement | null>>({});

  const menu = store?.menu || [];
  const categories = menu.map((c) => c.category);
  const specials = useMemo(() => menu.flatMap((c) => c.items.filter((i) => i.special).map((i) => ({ ...i, category: c.category }))), [menu]);

  // Chat "show_category"/"show_item" hand-offs arrive via location state.
  useEffect(() => {
    const st = (location.state || {}) as { category?: string; item?: string };
    if (st.category) {
      setExpanded((e) => new Set(e).add(st.category!));
      const el = refs.current[st.category];
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      if (st.item) {
        const c = menu.find((x) => x.category === st.category);
        const it = c?.items.find((i) => i.item.toLowerCase() === st.item!.toLowerCase());
        if (it) setDetail({ item: it, category: c!.category });
      }
    }
  }, [location.state, menu]);

  const searching = q.trim().length > 0;
  const toggle = (c: string) => setExpanded((e) => { const n = new Set(e); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const openAndScroll = (c: string) => {
    setExpanded((e) => new Set(e).add(c));
    setTimeout(() => refs.current[c]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const allOpen = categories.length > 0 && categories.every((c) => expanded.has(c));

  const filter = (i: MenuItem) => !q.trim() || i.item.toLowerCase().includes(q.trim().toLowerCase()) || (i.description || '').toLowerCase().includes(q.trim().toLowerCase());

  const add = (i: MenuItem) => {
    if (i.outOfStock) return;
    addToCart(i.item, 1);
    toast(`${i.item} added to cart`, 'ok');
  };

  if (!store) return null;
  return (
    <div className="menu">
      {specials.length > 0 && !q && (
        <section className="specials">
          <h2>Specials</h2>
          <div className="hscroll">
            {specials.map((i) => (
              <button key={`${i.category}/${i.item}`} className="special-card" onClick={() => setDetail({ item: i, category: i.category })}>
                {i.image && <img src={i.image} alt="" loading="lazy" />}
                <span className="special-name">{i.item}</span>
                {i.price && <span className="price">{i.price}</span>}
              </button>
            ))}
          </div>
        </section>
      )}
      <div className="menu-tools">
        <div className="chips">
          <button className="chip" onClick={() => setExpanded(allOpen ? new Set() : new Set(categories))}>{allOpen ? 'Collapse all' : 'Expand all'}</button>
          {categories.map((c) => (
            <button key={c} className={`chip ${expanded.has(c) ? 'chip-on' : ''}`} onClick={() => openAndScroll(c)}>{c}</button>
          ))}
        </div>
        <input className="input" placeholder="Search the menu" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the menu" />
      </div>
      {menu.map((c) => {
        const items = c.items.filter(filter);
        if (items.length === 0) return null;
        const open = searching || expanded.has(c.category);
        return (
          <section key={c.category} className={`menu-cat ${open ? 'open' : ''}`} ref={(el) => { refs.current[c.category] = el; }}>
            <button className="cat-head" aria-expanded={open} onClick={() => toggle(c.category)}>
              <h2>{c.category}</h2>
              <span className="muted small">{items.length}</span>
              <span className="chev" aria-hidden>›</span>
            </button>
            {open && <ul className="items">
              {items.map((i) => (
                <li key={i.item} className={`item ${i.outOfStock ? 'item-oos' : ''}`}>
                  <button className="item-main" onClick={() => setDetail({ item: i, category: c.category })}>
                    {i.image && <img className="item-thumb" src={i.image} alt="" loading="lazy" />}
                    <span className="item-text">
                      <span className="item-name">{i.item}{i.special && <span className="tag">Special</span>}{i.outOfStock && <span className="tag tag-muted">Sold out</span>}</span>
                      {i.description && <span className="item-desc">{i.description}</span>}
                    </span>
                  </button>
                  <div className="item-side">
                    <span className="price">{i.price || '—'}</span>
                    <button className="btn btn-sm" disabled={!!i.outOfStock || !i.price} onClick={() => add(i)} aria-label={`Add ${i.item} to cart`}>Add</button>
                  </div>
                </li>
              ))}
            </ul>}
          </section>
        );
      })}
      {menu.length === 0 && <p className="muted">This store has not published a menu yet.</p>}
      {detail && (
        <div className="sheet-backdrop" onClick={() => setDetail(null)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {detail.item.image && <img className="sheet-img" src={detail.item.image} alt="" />}
            <div className="sheet-body">
              <span className="muted small">{detail.category}</span>
              <h3>{detail.item.item}</h3>
              {detail.item.description && <p>{detail.item.description}</p>}
              <div className="row between">
                <span className="price big">{detail.item.price || 'Ask the store'}</span>
                <div className="row">
                  <button className="btn btn-ghost" onClick={() => setDetail(null)}>Close</button>
                  <button className="btn" disabled={!!detail.item.outOfStock || !detail.item.price} onClick={() => { add(detail.item); setDetail(null); }}>
                    {detail.item.outOfStock ? 'Sold out' : 'Add to cart'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
